import { Component, effect, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgxSpinnerModule } from 'ngx-spinner';
import { CopyHelper } from 'src/app/helpers/copy-helper';
import { PredefinedFilter } from 'src/app/models/predefined-filter';
import { ListLayout } from 'src/app/models/server/list-layout';
import { ProfileSettings } from 'src/app/models/server/profile-settings';
import { View } from 'src/app/models/server/view';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { ListLayoutsService } from 'src/app/services/http/list-layouts.service';
import { PredefinedFiltersService } from 'src/app/services/http/predefined-filters.service';
import { UsersService } from 'src/app/services/http/users.service';
import { ProfileSettingsStateService } from 'src/app/services/state/profile-settings-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';
import { SupervisionComponent } from '../supervision/supervision.component';
import { DialogProfileSettingsData } from './models/dialog-profile-settings-data';

import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { Rights } from 'src/app/models/server/enums/rights';
import { ProfileViewSettings } from 'src/app/models/server/profile-view-settings';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';

enum Loaded {
    NothingLoaded = 0,
    AccessibleViewsLoaded = 1 << 0, // 1,
    LayoutsLoaded = 1 << 1, // 2,
    UserDefaultViewLayoutLoaded = 1 << 2, // 4
    PredefinedFiltersLoaded = 1 << 3, // 8
}
@Component({
    selector: 'app-profile-settings',
    templateUrl: './profile-settings.component.html',
    styleUrls: ['./profile-settings.component.scss'],
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        MatOptionModule,
        MatCheckboxModule,
        NgxSpinnerModule,
        MatButtonModule,
        MatInputModule,
        MatTabsModule,
        MatSliderModule,
    ]
})
export class ProfileSettingsComponent implements OnInit {
    /**
     * Paramétrage des vues avant modification.
     */
    public originalViewSettings: Map<number, ProfileViewSettings>;

    /**
     * Paramétrage de la vue affichée dans l'onglet 'Paramétrage des vues'.
     */
    public selectedViewSettings: ProfileViewSettings = null;

    /**
     * Paramétrage de l'utilisateur.
     */
    public _settings: ProfileSettings;

    /**
     * Définit le Paramétrage de l'utilisateur.
     */
    public set settings(value: ProfileSettings) {
        this._settings = CopyHelper.cloneDeep(value);

        this.originalViewSettings = new Map<number, ProfileViewSettings>();
        this._settings.viewSettings.forEach((value, key) => {
            this.originalViewSettings.set(key, CopyHelper.cloneDeep(value));
        });
    }

    /**
     * Obtient le Paramétrage de l'utilisateur.
     */
    public get settings(): ProfileSettings {
        return this._settings;
    }

    /**
     * Liste des vues accessibles par l'utilisateur.
     */
    public accessibleViews: View[];

    /**
     * Liste des mises en page.
     */
    public layouts: ListLayout[];

    /*
     * Liste des filtres prédéfinis.
     */
    public predefinedFilters: PredefinedFilter[];

    /**
     * Etat de chargement des données à l'initialisation.
     */
    public initLoadedState = signal(Loaded.NothingLoaded);

    /**
     * Etat de chargement des données au changement de vue.
     */
    public viewLoadedState = signal(Loaded.NothingLoaded);

    /**
     * Valeur indiquant que toutes les données d'initialisation ont été chargées.
     */
    public initLoadingFinished: Loaded = Loaded.AccessibleViewsLoaded;

    /**
     * Valeur indiquant que toutes les données de la vue ont été chargées.
     */
    public viewLoadingFinished: Loaded = Loaded.LayoutsLoaded | Loaded.UserDefaultViewLayoutLoaded | Loaded.PredefinedFiltersLoaded;

    /**
     * Indique si l'utilisateur a le droit de suivre des dossiers.
     */
    public followFolderRight: boolean = false;

    /**
     * Champs du formulaire .
     */
    public defaultViewControl = new UntypedFormControl(null);
    public viewDefaultLayoutControl = new UntypedFormControl(null, Validators.required);
    public viewDefaultFilterControl = new UntypedFormControl(null);
    public sectionsGroupsCountControl = new UntypedFormControl(null);
    public autoFilterControl = new UntypedFormControl(null);
    public showFollowedFoldersControl = new UntypedFormControl(null);
    public viewControl = new UntypedFormControl(null);

    /**
     * Constructeur.
     */
    constructor(
        private profileSettingsStateService: ProfileSettingsStateService,
        private usersService: UsersService,
        private listLayoutService: ListLayoutsService,
        private predefinedFiltersService: PredefinedFiltersService,
        private viewsStateService: ViewsStateService,
        private spinner: SpinnerService,
        private connectedUserStateService: ConnectedUsersStateService,
        public dialogRef: MatDialogRef<SupervisionComponent>
    ) {
        this.followFolderRight = this.connectedUserStateService.hasRight(Rights.FollowFolder);

        effect(() => {
            if (this.viewLoadedState() === this.viewLoadingFinished) {
                // Cache le spinner lorsque toutes les données ont été chargées
                this.spinner.hide('profile-settings-spinner');
            } else if (this.initLoadedState() === this.initLoadingFinished) {
                // Cache le spinner lorsque toutes les données ont été chargées
                this.spinner.hide('profile-settings-spinner');
            }
        });
    }

    /**
     * Initialisation du composant.
     */
    ngOnInit(): void {
        this.spinner.show('profile-settings-spinner');
        this.loadSettings();
        this.loadAccessibleViews();
    }

    /**
     * Obtient une valeur indiquant si le formulaire est valide.
     */
    public get isValid(): boolean {
        return this.defaultViewControl.valid;
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Enregistrer'.
     */
    public save(): void {
        if (this.isValid) {
            let data = new DialogProfileSettingsData();
            data.settings = this.settings;

            if (data.settings.defaultViewId === -1) {
                data.settings.defaultViewId = null;
            }

            if (this.viewsStateService.getSelectedView()) {
                // Si une vue est selectionnée dans l'onglet Liste
                const listTabSelectedViewId = this.viewsStateService.getSelectedView().id;

                // Uniquement dans le cas où une vue est selectionnée
                if (
                    (this._settings.viewSettings.get(listTabSelectedViewId) &&
                        this._settings.viewSettings.get(listTabSelectedViewId) &&
                        this.originalViewSettings.get(listTabSelectedViewId)?.defaultViewLayout !== this._settings.viewSettings.get(listTabSelectedViewId)?.defaultViewLayout) ||
                    this.originalViewSettings.get(listTabSelectedViewId)?.defaultViewPredefinedFilter !==
                        this._settings.viewSettings.get(listTabSelectedViewId)?.defaultViewPredefinedFilter
                ) {
                    // Si la mise en page par défaut ou le filtre par défaut de la vue selectionnée dans l'onglet Liste a changé --> Indique que la vue doit être rechargée dans l'onglet Liste
                    data.needViewReloading = true;
                }
            }

            this.dialogRef.close(data);
        }
    }

    /**
     * Appellée lorsque l'utilisateur change le filtre par défaut.
     * @param predefinedFilterId : Id du filtre sélectionné.
     */
    public onViewPredefinedFilterChange(predefinedFilterId: number) {
        if (predefinedFilterId === -1) {
            this.selectedViewSettings.defaultViewPredefinedFilter = null;
        }
    }

    /**
     * Appellée lorsque l'utilisateur selectionne la vue à paramétrer.
     * @param viewId : Id de la vue à paramétrer.
     */
    public onViewChange(viewId: number) {
        this.spinner.show('profile-settings-spinner');
        this.viewLoadedState.set(Loaded.NothingLoaded);

        this.selectedViewSettings = this._settings.viewSettings.get(viewId);

        if (!this.selectedViewSettings) {
            // Aucun paramétrage n'existe pour cette vue --> création d'un paramétrage par défaut
            this.selectedViewSettings = new ProfileViewSettings();
            this._settings.viewSettings.set(viewId, this.selectedViewSettings);
        }

        this.loadLayouts(viewId);
        this.loadUserDefaultViewLayout(viewId);
        this.loadPredefinedFilters(viewId);
    }

    /**
     * Appellée lorsque l'utilisateur change le nombre de groupes de sections.
     * @param event
     */
    // public onSectionsGroupsCountChange(count: number) {
    //     this.data.duplicateCount = event.value;
    // }

    /**
     * Charge les paramètres de l'utilisateur.
     */
    private loadSettings() {
        this.settings = this.profileSettingsStateService.getProfileSettings();

        if (this.settings.defaultViewId === null) {
            this.settings.defaultViewId = -1;
        }
    }

    /**
     * Charge les vues accessibles par l'utilisateur.
     */
    private loadAccessibleViews() {
        this.usersService.getViews().subscribe((x) => {
            this.accessibleViews = x.sort((v1, v2) => v1.name.localeCompare(v2.name));
            this.initLoadedState.update((loadedState) => loadedState | Loaded.AccessibleViewsLoaded);
        });
    }

    /**
     * Charge les mises en page.
     * @param viewId : Identifiant de la vue.
     */
    private loadLayouts(viewId: number) {
        this.listLayoutService.List(viewId).subscribe((x) => {
            this.layouts = x.sort((v1, v2) => v1.name.localeCompare(v2.name));
            this.viewLoadedState.update((loadedState) => loadedState | Loaded.LayoutsLoaded);
        });
    }

    /**
     * Charge les filtres prédéfinis.
     * @param viewId : Identifiant de la vue.
     */
    private loadPredefinedFilters(viewId: number) {
        this.predefinedFiltersService.List(viewId).subscribe((x) => {
            this.predefinedFilters = x.sort((v1, v2) => v1.name.localeCompare(v2.name));
            this.viewLoadedState.update((loadedState) => loadedState | Loaded.PredefinedFiltersLoaded);
        });
    }

    /**
     * Charge la mise en page par défaut de l'utilisateur.
     * @param viewId : Identifiant de la vue.
     */
    private loadUserDefaultViewLayout(viewId: number) {
        this.usersService.getViewListLayout(viewId).subscribe((x) => {
            let defaultUserViewLayout = x.id;

            if (!this.selectedViewSettings.defaultViewLayout) {
                // Si l'utilisateur n'a pas spécifié une mise en page par défaut, on utilise celle défini par l'administrateur.
                this.selectedViewSettings.defaultViewLayout = defaultUserViewLayout;
            }

            this.viewLoadedState.update((loadedState) => loadedState | Loaded.UserDefaultViewLayoutLoaded);
        });
    }
}
